/**
 * SyncEngine — bridges localStorage cache and Workers /api/* (P5 L9).
 *
 * Pattern (controller décision) : simple debounced PUT + pull-merge on login + last-write-wins
 * by updatedAt. NO persisted offline queue (P5b consideration).
 *
 * Concrete usage :
 *   - On useAuth refresh → SyncEngine.pullAll() merges DB into localStorage (DB > local on conflict by ts)
 *   - On saveUserSettings/saveProgression → localStorage immediate, debounced 2s push to backend
 *   - Offline → drop pending push, retry on `window.online` event
 *   - Logout → reset queue + localStorage stays as last cache (cleared by deleteAllUserData if requested)
 */
import { apiGet, apiPost, apiPut, ApiError } from './api-client';
const DEBOUNCE_MS = 2000;
const listeners = new Set();
let state = {
    status: 'idle',
    lastSyncedAt: null,
    pendingChanges: 0,
    lastError: null,
};
const pendingPushes = new Map(); // dedupe by key
const debounceTimers = new Map();
let onlineHandlerInstalled = false;
function setState(patch) {
    state = { ...state, ...patch };
    listeners.forEach((l) => l(state));
}
function ensureOnlineHandler() {
    if (onlineHandlerInstalled || typeof window === 'undefined')
        return;
    onlineHandlerInstalled = true;
    window.addEventListener('online', () => {
        void flushAll();
    });
}
async function flushOne(key) {
    const fn = pendingPushes.get(key);
    if (!fn)
        return;
    pendingPushes.delete(key);
    setState({ status: 'syncing', pendingChanges: pendingPushes.size });
    try {
        await fn();
        setState({
            status: pendingPushes.size === 0 ? 'synced' : 'syncing',
            lastSyncedAt: new Date().toISOString(),
            lastError: null,
        });
    }
    catch (err) {
        if (err instanceof ApiError && err.status === 401) {
            // Session expired — drop the push, user will re-auth
            setState({ status: 'error', lastError: 'unauthorized' });
            pendingPushes.clear();
            return;
        }
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            setState({ status: 'offline' });
            // Re-queue
            pendingPushes.set(key, fn);
            return;
        }
        setState({
            status: 'error',
            lastError: err instanceof Error ? err.message : 'unknown',
        });
    }
}
async function flushAll() {
    const keys = Array.from(pendingPushes.keys());
    for (const k of keys) {
        await flushOne(k);
    }
}
function schedulePush(key, fn) {
    ensureOnlineHandler();
    pendingPushes.set(key, fn);
    setState({ pendingChanges: pendingPushes.size });
    const existing = debounceTimers.get(key);
    if (existing !== undefined) {
        window.clearTimeout(existing);
    }
    const t = window.setTimeout(() => {
        debounceTimers.delete(key);
        void flushOne(key);
    }, DEBOUNCE_MS);
    debounceTimers.set(key, t);
}
export const SyncEngine = {
    subscribe(listener) {
        listeners.add(listener);
        listener(state);
        return () => {
            listeners.delete(listener);
        };
    },
    getState() {
        return state;
    },
    /** Pull current user profile + consents + progression from DB. Caller merges into localStorage. */
    async pullAll() {
        setState({ status: 'syncing' });
        try {
            const [me, consents, progression] = await Promise.all([
                apiGet('/api/me')
                    .then((r) => r.user)
                    .catch((e) => {
                    if (e instanceof ApiError && e.status === 401)
                        return null;
                    throw e;
                }),
                apiGet('/api/consents')
                    .then((r) => r.consents)
                    .catch((e) => {
                    if (e instanceof ApiError && e.status === 401)
                        return [];
                    throw e;
                }),
                apiGet('/api/progression')
                    .then((r) => r.progression)
                    .catch((e) => {
                    if (e instanceof ApiError && e.status === 401)
                        return [];
                    throw e;
                }),
            ]);
            if (me === null) {
                setState({ status: 'idle', lastSyncedAt: null });
                return null;
            }
            setState({
                status: 'synced',
                lastSyncedAt: new Date().toISOString(),
                lastError: null,
            });
            return { me, consents, progression };
        }
        catch (err) {
            if (typeof navigator !== 'undefined' && !navigator.onLine) {
                setState({ status: 'offline' });
            }
            else {
                setState({
                    status: 'error',
                    lastError: err instanceof Error ? err.message : 'unknown',
                });
            }
            return null;
        }
    },
    /** Schedule a profile update push (debounced 2s). */
    pushMe(patch) {
        schedulePush('me', () => apiPut('/api/me', patch).then(() => undefined));
    },
    /** Append consent record to backend (immediate). */
    async pushConsent(consent) {
        try {
            await apiPost('/api/consents', {
                type: consent.type,
                granted: consent.granted,
                version: consent.version,
            });
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 401) {
                // ignore — user not signed in yet
                return;
            }
            throw err;
        }
    },
    /** Schedule a progression upsert (debounced 2s per slug). */
    pushProgression(p) {
        const key = `progression:${p.gameSlug}`;
        schedulePush(key, () => apiPut(`/api/progression/${encodeURIComponent(p.gameSlug)}`, {
            bestLevel: p.bestLevel,
            currentLevel: p.currentLevel,
            lastRunAt: p.lastRunAt,
            lastThreshold: p.lastThreshold,
        }).then(() => undefined));
    },
    /** Drop pending pushes (e.g. on signout). */
    reset() {
        pendingPushes.clear();
        debounceTimers.forEach((t) => {
            if (typeof window !== 'undefined')
                window.clearTimeout(t);
            else
                clearTimeout(t);
        });
        debounceTimers.clear();
        setState({ status: 'idle', pendingChanges: 0, lastError: null });
    },
    /** Test helper. */
    __resetForTests() {
        listeners.clear();
        pendingPushes.clear();
        debounceTimers.forEach((t) => {
            if (typeof window !== 'undefined')
                window.clearTimeout(t);
            else
                clearTimeout(t);
        });
        debounceTimers.clear();
        state = {
            status: 'idle',
            lastSyncedAt: null,
            pendingChanges: 0,
            lastError: null,
        };
        onlineHandlerInstalled = false;
    },
};
//# sourceMappingURL=sync.js.map